import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from '@dnd-kit/core';
import PageLayout from '../components/PageLayout';
import { useBackgroundFix } from '../utils/backgroundFix';

function DraggableMeal({ meal, dayDate }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `meal-${meal.mealPlanId}-${dayDate}`,
    data: { type: 'meal', meal, originalDate: dayDate }
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className="flex justify-between items-center text-sm bg-orange-50 rounded p-2 cursor-move hover:bg-orange-100 transition-colors"
    >
      <div>
        <span className="font-medium">{meal.recipeTitle}</span>
        <span className="text-gray-500 ml-2">({meal.mealType})</span>
      </div>
      {meal.nutrition && (
        <div className="text-xs text-gray-600">
          {meal.nutrition.calories ? `${Number(meal.nutrition.calories).toFixed(1)} cal` : '-'}
        </div>
      )}
    </div>
  );
}

function DroppableDay({ date, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${date}`,
    data: {
      type: 'day',
      date: date
    }
  });

  return (
    <div
      ref={setNodeRef}
      className={`bg-white rounded-lg p-4 border transition-all min-h-[150px] ${
        isOver ? 'border-orange-500 bg-orange-50 shadow-lg' : 'border-orange-200'
      }`}
    >
      {children}
    </div>
  );
}

function Nutrition() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().add(7, 'days').format('YYYY-MM-DD'));
  const [nutritionData, setNutritionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeMeal, setActiveMeal] = useState(null);

  // Fix background height on mobile
  useBackgroundFix('background-fixed', [nutritionData]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleFetchNutrition = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert('Start date must be before or equal to end date');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`/api/nutrition`, {
        params: {
          startDate,
          endDate
        }
      });
      setNutritionData(response.data);
    } catch (error) {
      console.error('Error fetching nutrition data:', error);
      alert('Failed to fetch nutrition data');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch nutrition data when dates change (skip initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (startDate && endDate && new Date(startDate) <= new Date(endDate)) {
      handleFetchNutrition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const formatDate = (dateStr) => {
    return moment(dateStr).format('MMMM D, YYYY');
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    return Number(num).toFixed(1);
  };

  const sortMealsByType = (meals) => {
    const mealOrder = ['breakfast', 'lunch', 'dinner', 'snack'];
    return [...meals].sort((a, b) => {
      const indexA = mealOrder.indexOf(a.mealType);
      const indexB = mealOrder.indexOf(b.mealType);
      // If meal type is not in the order array, put it at the end
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  const handleDragStart = (event) => {
    const { active } = event;
    if (active.data.current?.type === 'meal') {
      setActiveMeal(active.data.current.meal);
    }
  };

  const handleDragOver = (event) => {
    // This helps ensure drops are registered
    const { over } = event;
    if (over) {
      // Force the drop zone to be recognized
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveMeal(null);

    if (!over || !active.data.current) {
      console.log('No over or active data:', { over, active: active.data.current });
      return;
    }

    const mealData = active.data.current.meal;
    
    // Extract date from over - try multiple formats
    let targetDate = null;
    
    // Try different ways to get the date
    if (over.data?.current?.date) {
      targetDate = over.data.current.date;
    } else if (over.data?.date) {
      targetDate = over.data.date;
    } else if (typeof over.id === 'string' && over.id.startsWith('day-')) {
      targetDate = over.id.replace('day-', '');
    }

    console.log('Drag end:', { targetDate, mealPlanId: mealData?.mealPlanId, overId: over.id, overData: over.data });

    if (!targetDate || !mealData?.mealPlanId) {
      console.log('Missing target date or meal plan ID');
      return;
    }

    // Don't update if dropped on the same day
    if (targetDate === active.data.current.originalDate) {
      return;
    }

    // Ensure target date is within the selected range
    const targetMoment = moment(targetDate);
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    if (targetMoment.isBefore(startMoment, 'day') || targetMoment.isAfter(endMoment, 'day')) {
      alert('Can only move meals within the selected date range');
      return;
    }

    try {
      // Update the meal plan date
      await axios.put(`/api/meal-plans/${mealData.mealPlanId}`, {
        date: targetDate
      });

      // Refresh nutrition data
      await handleFetchNutrition();
    } catch (error) {
      console.error('Error updating meal plan:', error);
      alert('Failed to move meal. Please try again.');
    }
  };

  return (
    <>
      <div 
        id="background-fixed"
        className="fixed inset-0"
        style={{
          backgroundImage: 'url(/img/recipe-detail.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: -1
        }}
      />
      <style>{`
        @media (max-width: 767px) {
          #background-fixed {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            width: 100vw;
            min-height: 100vh;
            background-attachment: fixed;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
          }
        }
      `}</style>
      <PageLayout>
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 sm:mb-6 flex justify-between items-center">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">Nutrition Overview</h1>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-lg shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Select Date Range</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded"
                />
              </div>
            </div>
            <button
              onClick={handleFetchNutrition}
              disabled={loading}
              className="w-full md:w-auto px-6 py-3 bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              {loading ? 'Loading...' : 'Get Nutrition Data'}
            </button>
          </div>

          {nutritionData && (() => {
            const start = moment(startDate);
            const end = moment(endDate);
            const days = Math.max(1, end.diff(start, 'days') + 1);
            const average = {
              calories: nutritionData.total.calories / days,
              protein: nutritionData.total.protein / days,
              carbs: nutritionData.total.carbs / days,
              fat: nutritionData.total.fat / days,
              fiber: nutritionData.total.fiber / days,
              sugar: nutritionData.total.sugar / days,
              sodium: nutritionData.total.sodium / days
            };
            return (
              <>
                {/* Average Summary */}
                <div className="bg-white/60 backdrop-blur-sm rounded-lg shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold mb-4">Average Daily Nutrition ({formatDate(startDate)} - {formatDate(endDate)})</h2>
                  <div className="bg-orange-50 rounded-lg p-4 sm:p-6 border-2 border-orange-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Calories</div>
                        <div className="text-2xl font-bold text-orange-600">
                          {formatNumber(average.calories)}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Protein</div>
                        <div className="text-2xl font-bold text-orange-600">
                          {formatNumber(average.protein)}g
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Carbs</div>
                        <div className="text-2xl font-bold text-orange-600">
                          {formatNumber(average.carbs)}g
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Fat</div>
                        <div className="text-2xl font-bold text-orange-600">
                          {formatNumber(average.fat)}g
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-orange-300">
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Fiber</div>
                        <div className="text-xl font-bold text-orange-600">
                          {formatNumber(average.fiber)}g
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Sugar</div>
                        <div className="text-xl font-bold text-orange-600">
                          {formatNumber(average.sugar)}g
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Sodium</div>
                        <div className="text-xl font-bold text-orange-600">
                          {formatNumber(average.sodium)}mg
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600 mb-1">Total Meals</div>
                        <div className="text-xl font-bold text-orange-600">
                          {nutritionData.total.totalMeals}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Daily Breakdown */}
                {nutritionData.dailyBreakdown && nutritionData.dailyBreakdown.length > 0 && (
                  <DndContext
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="bg-white/60 backdrop-blur-sm rounded-lg shadow-lg p-6">
                      <h2 className="text-2xl font-bold mb-4">Daily Breakdown</h2>
                      <p className="text-sm text-gray-600 mb-4">Drag and drop meals between days to balance nutrition</p>
                      <div className="space-y-4">
                        {nutritionData.dailyBreakdown.map((day) => (
                          <DroppableDay key={day.date} date={day.date}>
                            <h3 className="text-lg font-bold mb-3">{formatDate(day.date)}</h3>
                            
                            {/* Daily Summary */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 pb-3 border-b">
                              <div>
                                <span className="text-sm text-gray-600">Calories: </span>
                                <span className="font-semibold">{formatNumber(day.calories)}</span>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Protein: </span>
                                <span className="font-semibold">{formatNumber(day.protein)}g</span>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Carbs: </span>
                                <span className="font-semibold">{formatNumber(day.carbs)}g</span>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Fat: </span>
                                <span className="font-semibold">{formatNumber(day.fat)}g</span>
                              </div>
                            </div>

                            {/* Meals List */}
                            {day.meals && day.meals.length > 0 && (
                              <div className="mt-3">
                                <div className="text-sm font-semibold mb-2 text-gray-700">Meals:</div>
                                <div className="space-y-2">
                                  {sortMealsByType(day.meals).map((meal, idx) => (
                                    <DraggableMeal key={meal.mealPlanId || idx} meal={meal} dayDate={day.date} />
                                  ))}
                                </div>
                              </div>
                            )}
                          </DroppableDay>
                        ))}
                      </div>
                    </div>
                    
                    <DragOverlay style={{ pointerEvents: 'none' }}>
                      {activeMeal ? (
                        <div className="flex justify-between items-center text-sm bg-orange-200 rounded p-2 shadow-lg">
                          <div>
                            <span className="font-medium">{activeMeal.recipeTitle}</span>
                            <span className="text-gray-500 ml-2">({activeMeal.mealType})</span>
                          </div>
                          {activeMeal.nutrition && (
                            <div className="text-xs text-gray-600">
                              {activeMeal.nutrition.calories ? `${formatNumber(activeMeal.nutrition.calories)} cal` : '-'}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                )}
            </>
            );
          })()}
        </div>
      </PageLayout>
    </>
  );
}

export default Nutrition;

